/**
 * Puffin Plastics estimated delivery dates.
 *
 * Stores hidden production metadata on each line item, then writes one visible
 * cart/order attribute using the longest lead time in the whole basket.
 * Working days exclude weekends and standard England & Wales bank holidays.
 */
(function (root, factory) {
  var api = factory();

  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.PuffinEstimatedDelivery = api;

  if (root && root.document && typeof root.fetch === 'function') api.install(root);
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var DATE_ATTRIBUTE = 'Estimated delivery date';
  var LEGACY_DATE_PROPERTY = 'Estimated delivery date';
  var LEAD_PROPERTY = '_Puffin lead working days';
  var CLASS_PROPERTY = '_Puffin production class';
  var UK_TIME_ZONE = 'Europe/London';

  function atNoonUtc(year, monthIndex, day) {
    return new Date(Date.UTC(year, monthIndex, day, 12, 0, 0));
  }

  function cloneDate(date) {
    return atNoonUtc(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }

  function isoDate(date) {
    return [
      date.getUTCFullYear(),
      String(date.getUTCMonth() + 1).padStart(2, '0'),
      String(date.getUTCDate()).padStart(2, '0')
    ].join('-');
  }

  function addCalendarDays(date, days) {
    var result = cloneDate(date);
    result.setUTCDate(result.getUTCDate() + days);
    return result;
  }

  function nthWeekdayOfMonth(year, monthIndex, weekday, nth) {
    var date = atNoonUtc(year, monthIndex, 1);
    var offset = (weekday - date.getUTCDay() + 7) % 7;
    date.setUTCDate(1 + offset + ((nth - 1) * 7));
    return date;
  }

  function lastWeekdayOfMonth(year, monthIndex, weekday) {
    var date = atNoonUtc(year, monthIndex + 1, 0);
    var offset = (date.getUTCDay() - weekday + 7) % 7;
    date.setUTCDate(date.getUTCDate() - offset);
    return date;
  }

  // Meeus/Jones/Butcher Gregorian Easter algorithm.
  function easterSunday(year) {
    var a = year % 19;
    var b = Math.floor(year / 100);
    var c = year % 100;
    var d = Math.floor(b / 4);
    var e = b % 4;
    var f = Math.floor((b + 8) / 25);
    var g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4);
    var k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * m + 114) / 31);
    var day = ((h + l - 7 * m + 114) % 31) + 1;
    return atNoonUtc(year, month - 1, day);
  }

  function addHoliday(set, date) {
    set[isoDate(date)] = true;
  }

  function addEnglandWalesHolidays(set, year) {
    var newYear = atNoonUtc(year, 0, 1);
    var newYearDay = newYear.getUTCDay();
    addHoliday(set, newYear);
    if (newYearDay === 6) addHoliday(set, atNoonUtc(year, 0, 3));
    if (newYearDay === 0) addHoliday(set, atNoonUtc(year, 0, 2));

    var easter = easterSunday(year);
    addHoliday(set, addCalendarDays(easter, -2));
    addHoliday(set, addCalendarDays(easter, 1));
    addHoliday(set, nthWeekdayOfMonth(year, 4, 1, 1));
    addHoliday(set, lastWeekdayOfMonth(year, 4, 1));
    addHoliday(set, lastWeekdayOfMonth(year, 7, 1));

    var christmas = atNoonUtc(year, 11, 25);
    var boxing = atNoonUtc(year, 11, 26);
    var christmasDay = christmas.getUTCDay();
    var boxingDay = boxing.getUTCDay();
    addHoliday(set, christmas);
    addHoliday(set, boxing);

    if (christmasDay === 6) addHoliday(set, atNoonUtc(year, 11, 27));
    if (christmasDay === 0) addHoliday(set, atNoonUtc(year, 11, 27));
    if (boxingDay === 6) addHoliday(set, atNoonUtc(year, 11, 28));
    if (boxingDay === 0) addHoliday(set, atNoonUtc(year, 11, 28));
  }

  function holidaySet(fromYear, toYear) {
    var set = Object.create(null);
    for (var year = fromYear; year <= toYear; year += 1) addEnglandWalesHolidays(set, year);

    // Confirmed exceptional dates published by GOV.UK. Keep this short list
    // alongside the calculated recurring bank holidays.
    ['2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25',
      '2026-08-31', '2026-12-25', '2026-12-28', '2027-01-01', '2027-03-26',
      '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27',
      '2027-12-28', '2028-01-03', '2028-04-14', '2028-04-17', '2028-05-01',
      '2028-05-29', '2028-08-28', '2028-12-25', '2028-12-26'].forEach(function (date) {
      set[date] = true;
    });

    return set;
  }

  function addWorkingDays(startDate, workingDays) {
    var result = cloneDate(startDate);
    var remaining = Math.max(0, parseInt(workingDays, 10) || 0);
    var holidays = holidaySet(result.getUTCFullYear(), result.getUTCFullYear() + 2);

    while (remaining > 0) {
      result = addCalendarDays(result, 1);
      var weekday = result.getUTCDay();
      if (weekday !== 0 && weekday !== 6 && !holidays[isoDate(result)]) remaining -= 1;
    }

    return result;
  }

  function ukToday(now) {
    var source = now || new Date();
    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: UK_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(source);
    var values = {};
    parts.forEach(function (part) { values[part.type] = part.value; });
    return atNoonUtc(Number(values.year), Number(values.month) - 1, Number(values.day));
  }

  function formatDate(date) {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  }

  function normalise(value) {
    return String(value == null ? '' : value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function propertyValue(properties, wantedKey) {
    var wanted = normalise(wantedKey);
    var keys = Object.keys(properties || {});
    for (var index = 0; index < keys.length; index += 1) {
      if (normalise(keys[index]) === wanted) return properties[keys[index]];
    }
    return '';
  }

  function fastProductionSelected(properties) {
    var keys = Object.keys(properties || {});
    return keys.some(function (key) {
      if (normalise(key).indexOf('fast production') === -1) return false;
      return /^(yes|true|1|selected|fast)\b/i.test(String(properties[key] == null ? '' : properties[key]).trim());
    });
  }

  function isBespoke(properties, calculatorPage) {
    if (calculatorPage) return true;
    if (normalise(propertyValue(properties, CLASS_PROPERTY)) === 'bespoke') return true;
    if (propertyValue(properties, '_Puffin_delivery_class')) return true;
    if (propertyValue(properties, 'Quoted price')) return true;
    return Object.keys(properties || {}).some(function (key) {
      return normalise(key).indexOf('fast production') !== -1;
    });
  }

  function leadWorkingDays(properties, calculatorPage) {
    if (!isBespoke(properties, calculatorPage)) return 2;
    return fastProductionSelected(properties) ? 11 : 15;
  }

  function estimate(properties, calculatorPage, baseDate) {
    var lead = leadWorkingDays(properties || {}, calculatorPage);
    var deliveryDate = addWorkingDays(baseDate || ukToday(), lead);
    return {
      className: lead === 2 ? 'stock' : 'bespoke',
      lead: lead,
      date: deliveryDate,
      formatted: formatDate(deliveryDate)
    };
  }

  function decorateLineItem(item, calculatorPage, baseDate) {
    if (!item || typeof item !== 'object') return item;
    var properties = item.properties && typeof item.properties === 'object' ? item.properties : {};
    var result = estimate(properties, calculatorPage, baseDate);
    delete properties[LEGACY_DATE_PROPERTY];
    properties[LEAD_PROPERTY] = String(result.lead);
    properties[CLASS_PROPERTY] = result.className;
    item.properties = properties;
    return item;
  }

  function lineItemLead(item) {
    var properties = item && item.properties && typeof item.properties === 'object' ? item.properties : {};
    var storedLead = parseInt(propertyValue(properties, LEAD_PROPERTY), 10);
    if (storedLead === 2 || storedLead === 11 || storedLead === 15) return storedLead;
    return leadWorkingDays(properties, false);
  }

  function estimateOrder(items, baseDate) {
    if (!Array.isArray(items) || !items.length) return null;
    var lead = items.reduce(function (longest, item) {
      return Math.max(longest, lineItemLead(item));
    }, 2);
    var deliveryDate = addWorkingDays(baseDate || ukToday(), lead);
    return {
      lead: lead,
      date: deliveryDate,
      formatted: formatDate(deliveryDate)
    };
  }

  function install(win) {
    var doc = win.document;
    var previousFetch = win.fetch;
    var bypassCheckout = false;
    var syncingForms = false;

    function calculatorPage() {
      return !!(doc.body && doc.body.classList.contains('pp-calculator-product'));
    }

    function isCartAddRequest(input) {
      var url = typeof input === 'string' ? input : input && input.url;
      return /\/cart\/add(?:\.js)?$/.test(String(url || '').split('?')[0]);
    }

    function decoratePayload(payload) {
      if (!payload || typeof payload !== 'object') return payload;
      if (Array.isArray(payload.items)) {
        payload.items.forEach(function (item) { decorateLineItem(item, calculatorPage()); });
      } else {
        decorateLineItem(payload, calculatorPage());
      }
      return payload;
    }

    function afterCartAdd(response) {
      if (!response || !response.ok) return response;
      return refreshCartEstimate().catch(function () {
        // The hidden line metadata is still retained if the attribute refresh fails.
      }).then(function () { return response; });
    }

    win.fetch = function (input, init) {
      if (!isCartAddRequest(input) || !init) return previousFetch.call(win, input, init);

      try {
        if (typeof init.body === 'string') {
          var payload = JSON.parse(init.body);
          var nextInit = Object.assign({}, init, { body: JSON.stringify(decoratePayload(payload)) });
          return previousFetch.call(win, input, nextInit).then(afterCartAdd);
        }

        if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
          var fake = { properties: {} };
          init.body.forEach(function (value, key) {
            var match = key.match(/^properties\[(.+)\]$/);
            if (match) fake.properties[match[1]] = value;
          });
          decorateLineItem(fake, calculatorPage());
          Object.keys(fake.properties).forEach(function (key) {
            init.body.set('properties[' + key + ']', fake.properties[key]);
          });
        }
      } catch (error) {
        // Never block a purchase because an unrelated add-to-cart payload is unusual.
      }

      return previousFetch.call(win, input, init).then(afterCartAdd);
    };

    function formProperties(form) {
      var properties = {};
      form.querySelectorAll('[name^="properties["]').forEach(function (field) {
        var match = field.name.match(/^properties\[(.+)\]$/);
        if (!match || ((field.type === 'checkbox' || field.type === 'radio') && !field.checked)) return;
        properties[match[1]] = field.value;
      });
      return properties;
    }

    function ensureHidden(form, propertyName, value) {
      var selector = 'input[name="properties[' + propertyName + ']"]';
      var input = form.querySelector(selector);
      if (!input) {
        input = doc.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + propertyName + ']';
        form.appendChild(input);
      }
      input.value = value;
    }

    function removeLegacyDateFields(form) {
      form.querySelectorAll('input[name="properties[' + LEGACY_DATE_PROPERTY + ']"]').forEach(function (input) {
        input.remove();
      });
    }

    function syncProductForm(form) {
      if (!form) return;
      var result = estimate(formProperties(form), !!form.querySelector('#calculator'));
      removeLegacyDateFields(form);
      ensureHidden(form, LEAD_PROPERTY, String(result.lead));
      ensureHidden(form, CLASS_PROPERTY, result.className);
    }

    function syncProductForms() {
      if (syncingForms) return;
      syncingForms = true;
      doc.querySelectorAll('form[action*="/cart/add"]').forEach(syncProductForm);
      syncingForms = false;
    }

    function refreshCartEstimate() {
      var cartUrl = (win.routes && win.routes.cartUrl) || '/cart';
      return previousFetch.call(win, cartUrl + '.js', {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(function (response) {
        if (!response.ok) throw new Error('Unable to read cart');
        return response.json();
      }).then(function (cart) {
        var chain = Promise.resolve();
        var preparedItems = [];
        (cart.items || []).forEach(function (item, index) {
          chain = chain.then(function () {
            var properties = Object.assign({}, item.properties || {});
            var fake = { properties: properties };
            decorateLineItem(fake, false);
            preparedItems.push(fake);
            if (JSON.stringify(properties) === JSON.stringify(item.properties || {})) return null;
            return previousFetch.call(win, cartUrl + '/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({
                line: index + 1,
                quantity: item.quantity,
                properties: properties
              })
            }).then(function (response) {
              if (!response.ok) throw new Error('Unable to update cart estimate');
            });
          });
        });
        return chain.then(function () {
          var orderEstimate = estimateOrder(preparedItems);
          var attributes = {};
          attributes[DATE_ATTRIBUTE] = orderEstimate ? orderEstimate.formatted : '';
          return previousFetch.call(win, cartUrl + '/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ attributes: attributes })
          }).then(function (response) {
            if (!response.ok) throw new Error('Unable to update order estimate');
            return orderEstimate;
          });
        });
      });
    }

    function checkoutControl(target) {
      return target && target.closest && target.closest(
        'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]'
      );
    }

    function addToCartControl(target) {
      return target && target.closest && target.closest(
        'form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] input[type="submit"]'
      );
    }

    doc.addEventListener('click', function (event) {
      var control = addToCartControl(event.target);
      if (!control) return;
      syncProductForm(control.form || control.closest('form[action*="/cart/add"]'));
    }, true);

    doc.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
      syncProductForm(form);
    }, true);

    doc.addEventListener('click', function (event) {
      var control = checkoutControl(event.target);
      if (!control || bypassCheckout) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      var wasDisabled = !!control.disabled;
      control.disabled = true;
      refreshCartEstimate().catch(function () {
        // Allow checkout even if the refresh request fails; existing metadata remains.
      }).then(function () {
        bypassCheckout = true;
        control.disabled = wasDisabled;
        if (control.tagName === 'A') {
          win.location.assign(control.href);
        } else {
          control.click();
        }
        win.setTimeout(function () { bypassCheckout = false; }, 1000);
      });
    }, true);

    doc.addEventListener('input', function () { win.setTimeout(syncProductForms, 0); }, true);
    doc.addEventListener('change', function () { win.setTimeout(syncProductForms, 0); }, true);

    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', syncProductForms);
    } else {
      syncProductForms();
    }

    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(function () { win.setTimeout(syncProductForms, 0); }).observe(doc.documentElement, {
        childList: true,
        subtree: true
      });
    }
  }

  return {
    DATE_ATTRIBUTE: DATE_ATTRIBUTE,
    LEGACY_DATE_PROPERTY: LEGACY_DATE_PROPERTY,
    LEAD_PROPERTY: LEAD_PROPERTY,
    CLASS_PROPERTY: CLASS_PROPERTY,
    addWorkingDays: addWorkingDays,
    decorateLineItem: decorateLineItem,
    estimate: estimate,
    estimateOrder: estimateOrder,
    formatDate: formatDate,
    holidaySet: holidaySet,
    install: install,
    isoDate: isoDate,
    leadWorkingDays: leadWorkingDays,
    ukToday: ukToday
  };
});
